import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Group,
  Stack,
  Text,
  Textarea,
} from "@mantine/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { v4 as uuid } from "uuid";
import moment from "moment-jalaali";
import { reviews, reviewKeys, useReviewCapabilities } from "@/services/reviews";
import {
  billingLabels,
  blockerLabel,
  hasReviewAction,
  isDefinitiveRejection,
  reviewError,
  sendInProgress,
  sendLabels,
} from "@/services/reviews/policy";
import useDashboardAccess from "@/hooks/useDashboardAccess";

const POLL_MS = 5000;

export default function InvitationStatus({
  purchaseId,
  hideWhenNotRequested = false,
}: {
  purchaseId: number;
  // Invoice details: no card at all for invoices without an invitation.
  hideWhenNotRequested?: boolean;
}) {
  const access = useDashboardAccess();
  const caps = useReviewCapabilities(access.isManager);
  const query = useQuery({
    queryKey: [...reviewKeys.all, "invitation", purchaseId],
    queryFn: () => reviews.invitation(purchaseId),
    enabled: access.isManager,
    retry: false,
    // Keep the status moving while the SMS is queued, scheduled or being sent.
    refetchInterval: (data) => (sendInProgress(data?.latestAttempt?.sendStatus) ? POLL_MS : false),
  });
  const client = useQueryClient();
  const [acknowledged, setAcknowledged] = useState(false);
  const [reason, setReason] = useState("");
  const pending = useRef<{ key: string; reason: string }>();
  const data = query.data;
  const retryAfter = data?.retryAfterUtc ? new Date(data.retryAfterUtc).getTime() : 0;
  const [now, setNow] = useState(Date.now());
  const cooling = retryAfter > now;
  // Tick only while a cooldown is running, and refresh once it ends.
  useEffect(() => {
    if (!cooling) return;
    const timer = setInterval(() => {
      const current = Date.now();
      setNow(current);
      if (current >= retryAfter) void query.refetch();
    }, 1000);
    return () => clearInterval(timer);
  }, [cooling, retryAfter]); // eslint-disable-line react-hooks/exhaustive-deps
  const retry = useMutation({
    mutationFn: async () => {
      const invitationId = data?.invitation?.invitationId;
      if (!invitationId) throw new Error("دعوت یافت نشد");
      // Same key only when repeating an attempt whose outcome is unknown.
      pending.current ??= { key: uuid(), reason: reason.trim() };
      return reviews.retry(invitationId, pending.current.reason, pending.current.key);
    },
    onSuccess: () => {
      pending.current = undefined;
      setAcknowledged(false);
      setReason("");
      void client.invalidateQueries({ queryKey: reviewKeys.all });
    },
    onError: (error) => {
      // Rejected by the server (cooldown, in progress, limit…): nothing was sent, next click is a new action.
      if (isDefinitiveRejection(error)) pending.current = undefined;
      void query.refetch();
    },
  });
  if (!access.isManager) return null;
  if (hideWhenNotRequested && data && !data.requested) return null;
  const attempt = data?.latestAttempt;
  const failures = [...new Set([data?.failureCode, attempt?.failureCode].filter(Boolean))];
  const mayRetry =
    data?.canRetry &&
    !cooling &&
    hasReviewAction(caps.data?.allowedActions, "retry", "reviews.sms.retry");
  const statusText = !data?.requested
    ? "برای این فاکتور درخواست نشده است"
    : attempt
      ? sendLabels[attempt.sendStatus ?? ""] || "وضعیت ارسال مشخص نیست"
      : failures.length
        ? "ارسال نشد"
        : "در انتظار آماده‌سازی";
  const unsure = !!pending.current && !retry.isLoading;
  return (
    <Card withBorder>
      <Stack gap="xs">
        <Group justify="space-between">
          <Text fw={700}>پیامک دریافت نظر</Text>
          {sendInProgress(attempt?.sendStatus) && <Badge variant="light">در حال پیگیری خودکار</Badge>}
        </Group>
        {query.isLoading && <Text size="sm">در حال دریافت وضعیت…</Text>}
        {query.isError && (
          <Alert color="red">
            {reviewError(query.error)}
            <Button variant="subtle" onClick={() => void query.refetch()}>
              تلاش مجدد
            </Button>
          </Alert>
        )}
        {data && (
          <>
            <Text size="sm">{statusText}</Text>
            {failures.map((code) => (
              <Text c="red" size="sm" key={code}>
                {blockerLabel(code)}
              </Text>
            ))}
            {attempt && (
              <Text size="xs" c="dimmed">
                تلاش {(data.attemptCount ?? 0).toLocaleString("fa-IR")}
                {attempt.cost != null && ` · هزینه ${attempt.cost.toLocaleString("fa-IR")} ریال`}
                {attempt.billingStatus && ` · ${billingLabels[attempt.billingStatus] || attempt.billingStatus}`}
                {attempt.scheduledForUtc && ` · ارسال در ${moment(attempt.scheduledForUtc).format("jYYYY/jMM/jDD HH:mm")}`}
              </Text>
            )}
            {data.invitation?.expiresAtUtc && (
              <Text size="xs">
                انقضا:{" "}
                {moment(data.invitation.expiresAtUtc).format("jYYYY/jMM/jDD HH:mm")}
              </Text>
            )}
            {data.retryDeniedReason && !failures.includes(data.retryDeniedReason) && (
              <Text size="xs">{blockerLabel(data.retryDeniedReason)}</Text>
            )}
            {cooling && (
              <Text size="xs">
                ارسال مجدد از{" "}
                {moment(data.retryAfterUtc).format("jYYYY/jMM/jDD HH:mm")} ممکن است
              </Text>
            )}
            {mayRetry && (
              <>
                <Textarea
                  label="دلیل ارسال مجدد"
                  value={unsure ? pending.current?.reason : reason}
                  maxLength={caps.data?.limits?.reasonMaxLength || 500}
                  onChange={(e) => setReason(e.currentTarget.value)}
                  disabled={retry.isLoading || unsure}
                />
                {unsure && (
                  <Text size="xs" c="dimmed">
                    نتیجه درخواست قبلی مشخص نشد؛ تکرار با همان درخواست انجام می‌شود و پیامک دوباره ارسال نمی‌شود.
                  </Text>
                )}
                {data.retryMayIncurCharge && (
                  <Checkbox
                    label="ارسال مجدد یک پیامک تازه است و دوباره هزینه دارد"
                    checked={acknowledged}
                    onChange={(e) => setAcknowledged(e.currentTarget.checked)}
                  />
                )}
                <Button
                  loading={retry.isLoading}
                  disabled={
                    !(unsure ? pending.current?.reason : reason.trim()) ||
                    (!!data.retryMayIncurCharge && !acknowledged)
                  }
                  onClick={() => retry.mutate()}
                >
                  ارسال مجدد پیامک
                </Button>
              </>
            )}
          </>
        )}
        {retry.isError && <Alert color="red">{reviewError(retry.error)}</Alert>}
        {retry.isSuccess && (
          <Alert color="green">
            درخواست ثبت شد: {sendLabels[retry.data.sendStatus ?? ""] || "در حال بررسی"}
          </Alert>
        )}
        <Group>
          <Button
            size="xs"
            variant="subtle"
            loading={query.isFetching}
            onClick={() => void query.refetch()}
          >
            به‌روزرسانی وضعیت
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}
